/**
 * Checks if a string is composed of numeric characters
 * @param str - The string to check
 * @returns true if the string contains only numeric characters, false otherwise
 */
export function isNumeric(str: string): boolean {
  if (str === null || str === undefined || str === '') {
    return false
  }

  // Regex for integer or float (with optional negative sign)
  const numericRegex = /^\d+$/
  return numericRegex.test(str.trim())
}

/**
 * Checks if a string represents a valid number
 * @param str - The string to check
 * @returns true if the string is a valid number, false otherwise
 */
export function isNumber(str: string): boolean {
  // Handle null, undefined, or empty string
  if (str === null || str === undefined || str === '') {
    return false
  }

  // Handle strings with only whitespace
  if (str.trim() === '') {
    return false
  }

  const stringValue = str.replace(',', '.')

  // Use Number constructor and isFinite to check for valid numbers
  // This handles integers, floats, scientific notation, negative numbers
  const num = Number(stringValue)

  // Check if conversion resulted in a finite number
  return Number.isFinite(num)
}

/**
 * Strict implementation using regex
 * Only allows pure numeric strings (integers and floats)
 * @param str - The string to check
 */
export function isNumberStrict(str: string): boolean {
  if (str === null || str === undefined || str === '') {
    return false
  }

  const stringValue = str.trim().replace(',', '.')

  // Regex for integer or float (with optional negative sign)
  const numberRegex = /^-?\d+(\.\d+)?$/
  return numberRegex.test(stringValue)
}

/**
 * Check if string is an integer
 * @param str - The string to check
 * @returns true if the string represents a valid integer, false otherwise
 */
export function isInteger(str: string, strict: boolean = true): boolean {
  if (!isNumber(str)) {
    return false
  }

  const num = Number(str)
  return !strict ? Number.isInteger(num) : Number.isInteger(num) && String(num) === str
}

// Default export is the main function
export default isNumber
